import { useEffect, useMemo, useState } from 'react';
import type { UserClaims } from '@okta/okta-auth-js';
import Modal from './Modal';
import { useAuth } from '../auth/useAuth';
import { getUserDisplayName, getUserEmail } from '../utils/userInfo';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { authState, oktaAuth } = useAuth();
    
  const [user, setUser] = useState<UserClaims | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  type OktaAuthWithGetUser = {
    getUser: () => Promise<UserClaims>;
  };
  type OktaAuthWithSignOut = {
    signOut: (options?: { postLogoutRedirectUri?: string }) => Promise<unknown>;
  };

  const hasGetUser = (auth: unknown): auth is OktaAuthWithGetUser =>
    Boolean(auth) && typeof (auth as OktaAuthWithGetUser).getUser === 'function';
  const hasSignOut = (auth: unknown): auth is OktaAuthWithSignOut =>
    Boolean(auth) && typeof (auth as OktaAuthWithSignOut).signOut === 'function';

  useEffect(() => {
    let isMounted = true;

    if (!isOpen) {
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (!authState?.isAuthenticated) {
      setUser(null);
      setError('Non sei autenticato.');
      return () => {
        isMounted = false;
      };
    }

    setIsLoading(true);
    setError(null);

    // Use the unified getUser method that handles debug mode transparently
    if (hasGetUser(oktaAuth)) {
      oktaAuth
        .getUser()
        .then((info: UserClaims) => {
          if (isMounted) {
            setUser(info);
          }
        })
        .catch((err: unknown) => {
          if (isMounted) {
            setError(err instanceof Error ? err.message : 'Impossibile caricare il profilo.');
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });
    } else {
      if (isMounted) {
        setError('Impossibile caricare il profilo.');
        setIsLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [authState, oktaAuth, isOpen]);

  const displayName = useMemo(() => {
    return getUserDisplayName(user, 'Utente');
  }, [user]);

  const email = getUserEmail(user);

  const handleLogout = async () => {
    try {
      if (hasSignOut(oktaAuth)) {
        await oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin });
      } else {
        setError('Impossibile terminare la sessione.');
      }
    } catch (logoutError) {
      // eslint-disable-next-line no-console
      console.error('Logout failed', logoutError);
      setError('Impossibile terminare la sessione.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="min-w-[320px] max-w-[480px] p-6">
        <h2 className="text-lg font-semibold text-gray-900">{displayName}</h2>
        {isLoading && <p className="mt-2 text-sm text-gray-600">Caricamento del profilo…</p>}
        {!isLoading && error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        {!isLoading && !error && email && <p className="mt-2 text-sm text-gray-600">{email}</p>}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={handleLogout}
            className="flex-1 bg-black px-4 py-2 text-sm font-semibold text-white border-2 border-black hover:bg-gray-900"
          >
            Esci
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-white px-4 py-2 text-sm font-semibold text-black border-2 border-black hover:bg-gray-100"
          >
            Chiudi
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ProfileModal;